import Plugin from '../Plugin'


export default class NPC extends Plugin {

    constructor(network) {
        super(network)
        this.events = {
            'npc_dialog': this.onNPCDialog.bind(this),
            'npc_dialog_update': this.onNPCDialogUpdate.bind(this),
            'npc_dialog_close': this.onNPCDialogClose.bind(this),
            'npc_quest_started': this.onQuestStarted.bind(this),
            'npc_quest_completed': this.onQuestCompleted.bind(this),
            'npc_reward': this.onNPCReward.bind(this),
            'npc_add': this.onNPCAdd.bind(this),
            'npc_remove': this.onNPCRemove.bind(this),
            'quest_objective_update': this.onQuestObjectiveUpdate.bind(this)
        }
    }

    /**
     * Handle NPC dialog event from server
     * Opens NPC dialog prompt with dialog data
     * @param {Object} args - { npcId, name, dialog, quests, activeQuests, completedQuests }
     */
    onNPCDialog(args) {
        console.log('[NPC Plugin] Opening NPC dialog:', args)
        
        // Sync active quests from server data
        this.syncActiveQuests(args.npcId, args.name, args.activeQuests, args.quests)
        
        // Sync completed quests from server data
        this.syncCompletedQuests(args.npcId, args.name, args.completedQuests, args.quests)
        
        this.interface.prompt.showNPCDialog(args)
    }

    /**
     * Handle dialog update from server (for dialog trees)
     * @param {Object} args - { dialog }
     */
    onNPCDialogUpdate(args) {
        console.log('[NPC Plugin] Dialog update:', args)
        this.interface.prompt.updateNPCDialog(args)
    }

    /**
     * Handle dialog close from server
     * @param {Object} args - {}
     */
    onNPCDialogClose(args) {
        console.log('[NPC Plugin] Closing dialog')
        this.interface.prompt.closeNPCDialog()
    }

    /**
     * Handle quest started notification
     * @param {Object} args - { npcId, quest, success, message }
     */
    onQuestStarted(args) {
        console.log('[NPC Plugin] Quest started:', args)
        
        if (!args.success) {
            console.log('[NPC Plugin] Quest start failed:', args.message)
            return
        }
        
        // Close the NPC dialog first
        this.interface.prompt.closeNPCDialog()
        
        // Extract quest info from the quest object
        const questName = args.quest?.name || args.questName || 'Unknown Quest'
        const questDescription = args.quest?.description || args.questDescription || ''
        
        // Show quest started notification
        const text = `New Quest: ${questName}\n\n${questDescription}`
        this.interface.prompt.showWindow(text, 'single')
        
        // Add quest to local tracking
        this.addActiveQuest(args.npcId, args.quest)
    }

    /**
     * Add a quest to local active quests tracking
     * @param {string} npcId - NPC ID
     * @param {Object} quest - Quest object
     */
    addActiveQuest(npcId, quest) {
        if (!quest) return
        
        // Initialize quests on world.client if not exists
        if (!this.world.client.activeQuests) {
            this.world.client.activeQuests = []
        }
        
        // Add quest if not already tracked
        const exists = this.world.client.activeQuests.find(q => q.id === quest.id)
        if (!exists) {
            this.world.client.activeQuests.push({
                ...quest,
                npcId: npcId,
                startedAt: Date.now()
            })
        }
    }

    /**
     * Handle quest completed notification
     * @param {Object} args - { npcId, questId, quest, rewards }
     */
    onQuestCompleted(args) {
        console.log('[NPC Plugin] Quest completed:', args)
        
        // Close the NPC dialog first
        this.interface.prompt.closeNPCDialog()
        
        // Extract quest name from quest object
        const questName = args.quest?.name || args.questName || 'Quest'
        
        // Show quest completed notification
        const rewardText = args.rewards ? this.formatRewards(args.rewards) : ''
        const text = `Quest Complete!\n${questName}${rewardText}`
        this.interface.prompt.showWindow(text, 'single')

        // Update client data with rewards
        if (args.rewards) {
            this.applyRewards(args.rewards)
        }
        
        // Remove from active quests
        this.removeActiveQuest(args.questId || args.quest?.id)
    }

    /**
     * Remove a quest from local active quests tracking and add to completed
     * @param {string} questId - Quest ID
     */
    removeActiveQuest(questId) {
        if (!questId || !this.world.client.activeQuests) return
        
        // Find the quest before removing
        const completedQuest = this.world.client.activeQuests.find(q => q.id === questId)
        
        // Remove from active quests
        this.world.client.activeQuests = this.world.client.activeQuests.filter(
            q => q.id !== questId
        )
        
        // Add to completed quests if found
        if (completedQuest) {
            if (!this.world.client.completedQuests) {
                this.world.client.completedQuests = []
            }
            
            // Check if already in completed
            const alreadyCompleted = this.world.client.completedQuests.find(q => q.id === questId)
            if (!alreadyCompleted) {
                this.world.client.completedQuests.push({
                    ...completedQuest,
                    completedAt: Date.now()
                })
            }
        }
    }

    /**
     * Handle NPC reward
     * @param {Object} args - { rewards }
     */
    onNPCReward(args) {
        console.log('[NPC Plugin] Received reward:', args)
        if (args.rewards) {
            this.applyRewards(args.rewards)
        }
    }

    /**
     * Handle NPC being added to room
     * @param {Object} args - NPC data
     */
    onNPCAdd(args) {
        console.log('[NPC Plugin] NPC added to room:', args)
        if (this.world.room.isReady) {
            this.world.room.addNPC(args)
        }
    }

    /**
     * Handle NPC being removed from room
     * @param {Object} args - { npcId }
     */
    onNPCRemove(args) {
        console.log('[NPC Plugin] NPC removed from room:', args)
        if (this.world.room.isReady) {
            this.world.room.removeNPC(args.npcId)
        }
    }

    /**
     * Handle quest objective update (e.g., when visiting a room completes an objective)
     * @param {Object} args - { npcId, npcName, questId, questName, objectiveId, objectiveDescription, completed, allObjectivesComplete }
     */
    onQuestObjectiveUpdate(args) {
        console.log('[NPC Plugin] Quest objective update:', args)
        
        // Update local quest tracking
        this.updateLocalQuestObjective(args)
        
        // Show notification to player
        if (args.completed) {
            const notification = `✅ Objective Complete!\n\n${args.objectiveDescription || 'Objective completed'}`
            this.interface.prompt.showWindow(notification, 'single')
            
            // If all objectives are complete, show additional notification
            if (args.allObjectivesComplete) {
                setTimeout(() => {
                    const readyNotification = `🎉 Quest Ready!\n\n"${args.questName || 'Quest'}"\n\nReturn to ${args.npcName || 'the NPC'} to claim your rewards!`
                    this.interface.prompt.showWindow(readyNotification, 'single')
                }, 2000)
            }
        }
    }

    /**
     * Update local quest objective tracking
     * @param {Object} args - Quest objective update data
     */
    updateLocalQuestObjective(args) {
        console.log('[NPC Plugin] updateLocalQuestObjective called with:', args)
        
        // Initialize if not exists
        if (!this.world.client.activeQuests) {
            this.world.client.activeQuests = []
        }
        
        let quest = this.world.client.activeQuests.find(q => q.id === args.questId)
        
        // If quest doesn't exist locally, add it with minimal info
        if (!quest) {
            console.log(`[NPC Plugin] Quest ${args.questId} not found locally, creating placeholder`)
            quest = {
                id: args.questId,
                name: args.questName || 'Unknown Quest',
                description: '',
                objectives: [],
                rewards: {},
                npcId: args.npcId,
                npcName: args.npcName,
                startedAt: Date.now()
            }
            this.world.client.activeQuests.push(quest)
        }
        
        // Initialize objectives array if needed
        if (!quest.objectives) {
            quest.objectives = []
        }
        
        // Find or create the objective
        let objective = quest.objectives.find(obj => 
            obj.id === args.objectiveId || 
            obj.description === args.objectiveDescription
        )
        
        if (!objective) {
            console.log(`[NPC Plugin] Objective ${args.objectiveId} not found, creating it`)
            objective = {
                id: args.objectiveId,
                description: args.objectiveDescription || 'Unknown objective',
                completed: false
            }
            quest.objectives.push(objective)
        }
        
        // Update the objective
        objective.completed = args.completed
        objective.completedAt = Date.now()
        console.log(`[NPC Plugin] Updated objective ${args.objectiveId} for quest ${args.questId}: completed=${args.completed}`)
    }

    /**
     * Format rewards for display
     * @param {Object} rewards - { coins, items, etc }
     * @returns {string}
     */
    formatRewards(rewards) {
        const parts = []
        
        if (rewards.coins) {
            parts.push(`+${rewards.coins} coins`)
        }
        if (rewards.items && rewards.items.length > 0) {
            const itemNames = rewards.items.map(id => {
                const item = this.crumbs.items[id]
                return item ? item.name : `Item #${id}`
            })
            parts.push(`Items: ${itemNames.join(', ')}`)
        }
        if (rewards.furniture && rewards.furniture.length > 0) {
            parts.push(`+${rewards.furniture.length} furniture`)
        }

        return parts.length > 0 ? `\nRewards: ${parts.join(', ')}` : ''
    }

    /**
     * Apply rewards to client
     * @param {Object} rewards - { coins, items, furniture }
     */
    applyRewards(rewards) {
        if (rewards.coins) {
            this.world.client.coins += rewards.coins
            // Show coin prompt with skipTeleport=true so it doesn't teleport player after closing
            this.interface.prompt.coin.show(rewards.coins, 0, 0, undefined, [], false, true)
        }

        if (rewards.items && rewards.items.length > 0) {
            rewards.items.forEach(itemId => {
                const item = this.crumbs.items[itemId]
                if (item && !this.world.client.inventory[item.slot].includes(itemId)) {
                    this.world.client.inventory[item.slot].push(itemId)
                    this.interface.prompt.showItem(itemId)
                }
            })
        }

        if (rewards.furniture && rewards.furniture.length > 0) {
            rewards.furniture.forEach(furnId => {
                if (!this.world.client.furniture.includes(furnId)) {
                    this.world.client.furniture.push(furnId)
                    this.interface.prompt.showFurniture(furnId)
                }
            })
        }
    }

    /**
     * Sync active quests from server NPC data
     * @param {string} npcId - NPC ID
     * @param {string} npcName - NPC name
     * @param {Array} activeQuests - Array of active quest IDs/objects from server (includes progress data)
     * @param {Array} quests - Full quest definitions from NPC
     */
    syncActiveQuests(npcId, npcName, activeQuests, quests) {
        if (!activeQuests || !Array.isArray(activeQuests)) return
        
        // Initialize if not exists
        if (!this.world.client.activeQuests) {
            this.world.client.activeQuests = []
        }
        
        activeQuests.forEach(activeQuest => {
            const questId = activeQuest.questId || activeQuest.id || activeQuest
            
            // Check if already tracked
            const existingIndex = this.world.client.activeQuests.findIndex(q => q.id === questId)
            
            // Find full quest definition
            let questDef = null
            if (quests && Array.isArray(quests)) {
                questDef = quests.find(q => q.id === questId)
            }
            
            // Build objectives with progress merged in
            let objectives = []
            if (questDef?.objectives) {
                objectives = questDef.objectives.map(obj => {
                    const objId = obj.id || `obj_${questDef.objectives.indexOf(obj)}`
                    const objProgress = activeQuest.progress?.objectives?.[objId]
                    
                    return {
                        ...obj,
                        id: objId,
                        completed: objProgress?.completed || false,
                        completedAt: objProgress?.completedAt || null
                    }
                })
            }
            
            const questData = {
                id: questId,
                name: questDef?.name || 'Unknown Quest',
                description: questDef?.description || '',
                objectives: objectives,
                rewards: questDef?.rewards || {},
                npcId: npcId,
                npcName: npcName,
                startedAt: activeQuest.startedAt || Date.now()
            }
            
            if (existingIndex >= 0) {
                // Update existing quest with fresh data
                this.world.client.activeQuests[existingIndex] = questData
            } else {
                // Add new quest
                this.world.client.activeQuests.push(questData)
            }
            
            console.log(`[NPC Plugin] Synced quest ${questId}:`, questData)
        })
    }

    /**
     * Sync completed quests from server NPC data
     * @param {string} npcId - NPC ID
     * @param {string} npcName - NPC name
     * @param {Array} completedQuests - Array of completed quest IDs from server
     * @param {Array} quests - Full quest definitions from NPC
     */
    syncCompletedQuests(npcId, npcName, completedQuests, quests) {
        if (!completedQuests || !Array.isArray(completedQuests)) return
        
        // Initialize if not exists
        if (!this.world.client.completedQuests) {
            this.world.client.completedQuests = []
        }
        
        completedQuests.forEach(questId => {
            // Check if already tracked
            const exists = this.world.client.completedQuests.find(q => q.id === questId)
            if (exists) return
            
            // Find full quest definition
            let questDef = null
            if (quests && Array.isArray(quests)) {
                questDef = quests.find(q => q.id === questId)
            }
            
            // Add to completed quests
            this.world.client.completedQuests.push({
                id: questId,
                name: questDef?.name || 'Unknown Quest',
                description: questDef?.description || '',
                objectives: questDef?.objectives || [],
                rewards: questDef?.rewards || {},
                npcId: npcId,
                npcName: npcName,
                completedAt: Date.now()
            })
        })
    }

}
